import { z } from "zod";
import { queueQuery } from "./requests.js";
import { assetClassFilters, priorities, type AssetClassFilter, type Priority } from "./vocabulary.js";
import type { QueueRow } from "./wire.js";

/** Shared scopes apply before either the board's first page or a cell's later pages. */
const kanbanScopes = {
  repairKind: z.enum(["INTERNAL", "VENDOR"]).optional(),
  vendorId: z.string().uuid().optional(),
  mechanicId: z.string().uuid().optional(),
  categoryIds: queueQuery.shape.categoryIds,
};

/** One independently paged, authorization-filtered work-order cell. */
export const kanbanQuery = z.object({
  ...kanbanScopes,
  assetClass: z.enum(assetClassFilters),
  priority: z.enum(priorities),
  status: z.enum(["open", "completed", "cancelled"]).default("open"),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
  offset: z.coerce.number().int().min(0).default(0),
}).strict();
export type KanbanQuery = z.infer<typeof kanbanQuery>;
/** URL transport: buildUrl accepts the canonical category list as CSV. */
export type KanbanQueryParams = Omit<KanbanQuery, "categoryIds"> & { categoryIds?: string };
export interface KanbanCellResponse {
  items: QueueRow[];
  total: number;
}

/**
 * Every cell of the board in ONE request — TRBUD rider 4.4.
 *
 * `kanbanQuery` answers a single cell, and the board has one per class tab and priority (five
 * class tabs x four priorities = twenty since TRBUD-348 added company
 * vehicles; it was sixteen). Mounting the screen therefore fired one request per cell,
 * each re-resolving the session, re-reading the pipeline kinds and
 * opening its own round trip — and then did it again every 60 seconds.
 *
 * ## Why this does not simply replace the per-cell route
 *
 * Each cell pages INDEPENDENTLY: a reader can be on page 3 of Trucks / Urgent
 * while every other cell sits on page 1. Folding that into one request would mean
 * carrying sixteen offsets in the query string, and a board request would then
 * change shape every time anybody pressed Next.
 *
 * So the split is by what is actually being asked. This answers "the first page
 * of all sixteen", which is the mount and the refetch — the whole of the storm.
 * A cell that has been paged past its first page keeps asking `kanbanQuery` for
 * itself alone, which is one request for one deliberate action.
 *
 * No `offset`, deliberately: a board request is always the first page, and
 * accepting an offset would invite a caller to think it pages the board.
 */
export const kanbanBoardQuery = z.object({
  ...kanbanScopes,
  status: z.enum(["open", "completed", "cancelled"]).default("open"),
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(10),
}).strict();
export type KanbanBoardQuery = z.infer<typeof kanbanBoardQuery>;
export type KanbanBoardQueryParams = Omit<KanbanBoardQuery, "categoryIds"> & { categoryIds?: string };

export interface KanbanBoardCell extends KanbanCellResponse {
  assetClass: AssetClassFilter;
  priority: Priority;
}

export interface KanbanBoardResponse {
  cells: KanbanBoardCell[];
}

/** Every cell (one per class and priority), in the order the board renders them. */
export function kanbanBoardCellKeys(): Array<{ assetClass: AssetClassFilter; priority: Priority }> {
  const out: Array<{ assetClass: AssetClassFilter; priority: Priority }> = [];
  for (const assetClass of assetClassFilters) {
    for (const priority of priorities) out.push({ assetClass, priority });
  }
  return out;
}
